import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { FlowContentNodeSchema, type FlowContentNode } from "../nodes";
import {
  ToolRegistryItemSchema,
  VariableRegistryItemSchema,
  type ToolRegistryItem,
  type VariableRegistryItem,
} from "./registry-schema";

/**
 * Current schema version for procedures.
 * Increment when making breaking changes to the schema.
 */
export const PROCEDURE_SCHEMA_VERSION = 1;

/**
 * Embedding with checksum for change detection.
 */
const EmbeddingWithChecksumSchema_ = z.object({
  embedding: z.array(z.number()).optional(),
  checksum: z.string().optional(),
});
export const EmbeddingWithChecksumSchema: z.ZodType<EmbeddingWithChecksum> =
  EmbeddingWithChecksumSchema_;
export interface EmbeddingWithChecksum
  extends z.infer<typeof EmbeddingWithChecksumSchema_> {}

/**
 * Embeddings container for procedure.
 */
const ProcedureEmbeddingsSchema_ = z.object({
  searchText: EmbeddingWithChecksumSchema.optional(),
});
export const ProcedureEmbeddingsSchema: z.ZodType<ProcedureEmbeddings> =
  ProcedureEmbeddingsSchema_;
export interface ProcedureEmbeddings
  extends z.infer<typeof ProcedureEmbeddingsSchema_> {}

/**
 * Procedure document schema.
 */
const ProcedureSchema_ = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),

  // Metadata
  name: z.string(),
  description: z.string().optional(),
  schemaVersion: z.number().default(PROCEDURE_SCHEMA_VERSION),

  // Whether this procedure is enabled (defaults to true)
  enabled: z.boolean().default(true),

  // Content - the markdown-flow JSON array
  content: z.array(FlowContentNodeSchema),

  // Auto-computed registries (extracted from content on save)
  variableRegistry: z.array(VariableRegistryItemSchema),
  toolRegistry: z.array(ToolRegistryItemSchema),

  // Embeddings for semantic search
  embeddings: ProcedureEmbeddingsSchema.optional(),

  // Timestamps
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ProcedureSchema: z.ZodType<Procedure> = ProcedureSchema_;
export interface Procedure {
  _id: string;
  team: string;
  name: string;
  description?: string;
  schemaVersion: number;
  enabled: boolean;
  content: FlowContentNode[];
  variableRegistry: VariableRegistryItem[];
  toolRegistry: ToolRegistryItem[];
  embeddings?: ProcedureEmbeddings;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Schema for creating a new procedure (without _id and timestamps).
 */
const CreateProcedureInputSchema_ = z.object({
  name: z.string(),
  description: z.string().optional(),
  content: z.array(FlowContentNodeSchema),
  enabled: z.boolean().optional(),
});
export const CreateProcedureInputSchema: z.ZodType<CreateProcedureInput> =
  CreateProcedureInputSchema_;
export interface CreateProcedureInput
  extends z.infer<typeof CreateProcedureInputSchema_> {}

/**
 * Schema for updating an existing procedure.
 */
const UpdateProcedureInputSchema_ = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  content: z.array(FlowContentNodeSchema).optional(),
  enabled: z.boolean().optional(),
});
export const UpdateProcedureInputSchema: z.ZodType<UpdateProcedureInput> =
  UpdateProcedureInputSchema_;
export interface UpdateProcedureInput
  extends z.infer<typeof UpdateProcedureInputSchema_> {}
